import { DateColumnHeader } from './DateColumnHeader';
import { calendarDate, formatAppDateTime } from '../utility/dateLocale';
import { OpportunityQuoteModal } from './OpportunityQuoteModal';
import React, { useState, useEffect, useMemo } from 'react';
import { OpportunityProductsModal } from './OpportunityProductsModal';
import { SalesPackageTable } from './SalesPackageTable';
import { belongsToPackage } from '../utility/salesPackages';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Group,
  Modal,
  Button,
  Text,
  ActionIcon,
  Tabs,
  Table,
  Select,
  MultiSelect,
  TextInput,
  useMantineTheme,
  Stack,
  Checkbox,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import {
  IconFileExcel,
  IconCloudUpload,
  IconPlus,
  IconFile
} from '@tabler/icons-react';
import { useDropzone, FileRejection } from 'react-dropzone';
import * as XLSX from 'xlsx';
import '../pages/KanbanColumn.css';
import './OpportunityDetails.css';
import {
  Product,
  Opportunity,
  OpportunityForm,
  OpportunityFile,
  initialOpportunityForm,
  STAGES,
} from './OpportunityPageTypes';
import { getCurrentStageForOpportunity } from './OpportunityPageUtilities';
import { getStageLabel } from '../i18n/stageLabels';
import { useAuth } from '../contexts/AuthProvider';
import { AttachmentActionButtons } from './AttachmentActionButtons';
import { useAttachmentPreview } from './AttachmentPreviewModal';
import {
  attachmentSourceFromFields,
  downloadAttachment,
  type AttachmentFileSource,
} from '../utility/openRemoteFile';
import { buildPageUrl } from '../utility/pageNavigationFilters';
import { Options } from '../utility/types';
export type NewContactData = {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  position: string;
  client_ids?: string[];
};

export type OpportunityModalProps = {
  opened: boolean;
  onClose: () => void;
  onSave: (opp: OpportunityForm) => void;
  onUpdate: (opp: OpportunityForm, id: string) => void;
  onDelete?: () => void;
  removeFiles: (id: string) => void;
  productOptions: Options[];
  products: Product[];
  clientOptions?: Options[];
  contactOptions?: Options[];
  campaignOptions?: Options[];
  ownerOptions?: Options[];
  editingOpportunity?: Opportunity | null;
  onCreateContact?: (data: NewContactData) => Promise<Options>;
};

export const OpportunityModal: React.FC<OpportunityModalProps> = ({
  opened,
  onClose,
  onSave,
  onUpdate,
  onDelete,
  removeFiles,
  productOptions,
  products,
  clientOptions,
  contactOptions,
  campaignOptions,
  ownerOptions,
  editingOpportunity,
  onCreateContact,
}) => {
  useEffect(() => { if (!opened) setQuoteOpened(false); }, [opened]);
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { accessToken, user } = useAuth();
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<string | null>(null);
  const { openPreview, previewModal } = useAttachmentPreview(accessToken);

  const stageOptions = useMemo(
    () => STAGES.map((stage) => ({ value: stage, label: getStageLabel(stage, t) })),
    [t, i18n.language],
  );

  const [modalMode, setModalMode] = useState<
    'manual' | 'automático' | 'anexos'
  >('manual');
  const [oppForm, setOppForm] = useState<OpportunityForm>(
    initialOpportunityForm
  );
  const [autoImportFile, setAutoImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [notification, setNotification] = useState<{
    message: string;
    color: 'green' | 'red';
  } | null>(null);
  const [quoteOpened, setQuoteOpened] = useState(false);
  const [productModalOpened, setProductModalOpened] = useState(false);

  const [influencerOptions, setInfluencerOptions] = useState<Options[]>([]);
  const [investidorOptions, setInvestidorOptions] = useState<Options[]>([]);
  const [decisionMakerOptions, setDesisionMakerOptions] = useState<Options[]>([]);
  const [contactModalOpened, setContactModalOpened] = useState(false);
  const [newContactForm, setNewContactForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    position: '',
  });
  const [contactSaving, setContactSaving] = useState(false);
  const [initialPackage, setInitialPackage] = useState<Product[] | null>(null);
  const [_, setNoSalesMade] = useState(false);

  // state to hold attachments
  const [attachmentErrors, setAttachmentErrors] = useState<string[]>([]);

  // dropzone for auto-import oportunidade
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => {
      if (files.length) setAutoImportFile(files[0]);
    },
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxSize: 30 * 1024 * 1024,
  });

  // dropzone for anexos
  const {
    getRootProps: getRootPropsAnexos,
    getInputProps: getInputPropsAnexos,
    isDragActive: isDragActiveAnexos,
  } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
    maxSize: 50 * 1024 * 1024,
    onDrop: (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const newErrors: string[] = [];
      const newAttachments = [...oppForm.files];
      const availableSlots = 10 - oppForm.files.length;

      console.log('Acepted files:', acceptedFiles);

      if (acceptedFiles.length > availableSlots) {
        newErrors.push(
          `Você pode anexar no máximo 10 arquivos. ${acceptedFiles.length - availableSlots
          } arquivo(s) não foram adicionados.`
        );
      }
      console.log('Accepted files:', acceptedFiles);

      acceptedFiles.slice(0, availableSlots).forEach((file) => {
        // const url = URL.createObjectURL(file);
        newAttachments.push({
          id: Date.now().toString() + Math.random(),
          opportunity_id: editingOpportunity ? editingOpportunity.id : '',
          file_name: file.name,
          file: file,
          created_at: new Date().toISOString(),
          size: file.size,
        });
      });

      fileRejections.forEach(({ file, errors }) => {
        errors.forEach((e) => {
          if (e.code === 'file-too-large') {
            newErrors.push(
              `${file.name} excede o tamanho máximo (50MB).`
            );
          } else if (e.code === 'file-invalid-type') {
            newErrors.push(`${file.name} não é um PDF válido.`);
          } else {
            newErrors.push(`${file.name}: ${e.message}`);
          }
        });
      });

      setAttachmentErrors(newErrors);
      setOppForm({ ...oppForm, files: newAttachments });
    },
  });

  const removeAttachment = (id: string) => {
    // setAttachments((prev) =>
    //   prev.filter((att) => {
    //     if (att.id === id) {
    //       URL.revokeObjectURL(att.url);
    //       return false;
    //     }
    //     return true;
    //   })
    // );
    const attachment = oppForm.files.find((att) => att.id === id);
    if (attachment && typeof attachment.file === 'string') {
      removeFiles(attachment.file);
    }
    setOppForm((prev) => ({
      ...prev,
      files: prev.files.filter((att) => att.id !== id),
    }));
  };

  useEffect(() => {
    if (!opened) {
      return;
    }
    if (editingOpportunity) {
      setOppForm(editingOpportunity);
      return;
    }
    setOppForm({
      ...initialOpportunityForm,
      user_id: user?.id ?? initialOpportunityForm.user_id,
      owner: user?.id ?? null,
      startDate: new Date().toISOString(),
    });
  }, [editingOpportunity, opened, user?.id]);

  useEffect(() => {
    if (!productModalOpened) {
      updateOpportunityValue();

    }
  }, [productModalOpened]);

  const handleAutoImport = async () => {
    if (autoImportFile) {
      try {
        const data = await autoImportFile.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rawData = XLSX.utils.sheet_to_json(sheet);
        const errors: string[] = [];
        const validOpps: OpportunityForm[] = [];

        rawData.forEach((row: any, index: number) => {
          const opp: OpportunityForm = {
            name: row['Nome'] || '',
            client: row['Client'] || '',
            client_ids: row['Customer'] ? [String(row['Customer'])] : [],
            contact_id: row['Contact'] || '',
            campaign_id: row['Campaign'] || '',
            user_id: 'user1',
            value: parseFloat(row['Value'] || '0'),
            probability: parseFloat(row['Probability'] || '0'),
            discount: parseFloat(row['Discount'] || '0'),
            decision_maker: row['Decision Maker'] || '',
            influencer: row['Influencer'] || '',
            investidor: row['Investidor'] || '',
            files: [],
            remito: row['Remito'] || '',
            stages: [
              {
                id: `s-${index + 1}`,
                opportunity_id: '',
                name: row['Stage'] || 'Proposta de Valor',
                created_at: row['Start Date'] || '',
              },
            ],
            startDate: row['Start Date'] || '',
            products: [],
            client_image: '',
          };
          const rowErrors: string[] = [];
          if (!opp.name) rowErrors.push('Nome é obrigatório');
          if (!opp.client) rowErrors.push('Client é obrigatório');
          if (!opp.value) rowErrors.push('Value é obrigatório');
          if (!opp.decision_maker)
            rowErrors.push('Decision Maker é obrigatório');
          if (!opp.influencer) rowErrors.push('Influencer é obrigatório');
          if (!opp.investidor) rowErrors.push('Investidor é obrigatório');
          if (!opp.startDate) rowErrors.push('Start Date é obrigatório');
          if (rowErrors.length > 0) {
            errors.push(
              `Erro na linha ${index + 2}: ${rowErrors.join(' ')}`
            );
          } else {
            validOpps.push(opp);
          }
        });

        if (errors.length > 0) {
          setNotification({
            message: 'Erro ao importar algumas oportunidades',
            color: 'red',
          });
          setImportErrors(errors);
        } else {
          onSave(validOpps[0]);
          setNotification({
            message: 'Oportunidade importada com sucesso',
            color: 'green',
          });
          onClose();
        }
      } catch (error) {
        console.error('Erro ao processar arquivo:', error);
        setNotification({
          message: 'Erro ao processar arquivo',
          color: 'red',
        });
      }
    } else {
      console.log('Nenhum arquivo selecionado');
    }
  };

  const validateFileds = () => {
    const closingNow =
      (oppForm.stages.find((st) => st.finished_at == null)?.name || '').toLowerCase() === 'closed';
    return (
      !oppForm.name ||
      !oppForm.owner ||
      !oppForm.contact_id ||
      !oppForm.startDate ||
      (closingNow && !oppForm.client_ids.length)
    );
  };

  const saveOpportunity = () => {
    onSave(oppForm);
  };

  const generateExcelTemplate = () => {
    const worksheetData = [
      [
        'Nome',
        'Responsável',
        'Oportunidade/Cliente',
        'Tipo de Atividade',
        'Due Date',
        'Description',
      ],
      [
        'Atividade Exemplo',
        'João Silva',
        'Cliente A',
        'Ligação',
        '2025-01-15',
        'Fazer follow-up com o cliente',
      ],
      [
        'Atividade Exemplo 2',
        'Maria Souza',
        'Cliente Y',
        'Reunião',
        '2025-02-01',
        'Reunião para apresentação da proposta',
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
    ws['dataValidations'] = [
      {
        sqref: 'D2:D1048576',
        type: 'list',
        formula1:
          '"demonstração,mensagem,email,reunião presencial,reunião online"',
        showErrorMessage: true,
        errorTitle: 'Erro de Validação',
        error: 'Por favor, insira um dos valores permitidos.',
      },
      {
        sqref: 'E2:E1048576',
        type: 'date',
        showErrorMessage: true,
        errorTitle: 'Erro de Validação',
        error: 'Por favor, insira uma data válida (YYYY-MM-DD).',
      },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'template_tasks.xlsx';
    link.click();
  };

  const updateOpportunityValue = () => {
    let price = 0.0;
    oppForm.products.forEach((product) => {
      const productValue =
        product.price * product.quantity * (1 - product.discount / 100);
      price += productValue;
    });
    setOppForm({ ...oppForm, value: Math.round(price * 100) / 100 });
    console.log('Updated Opportunity Value:', price);
  };

  useEffect(() => {
    if (opened) {
      setDesisionMakerOptions(contactOptions || []);
      setInfluencerOptions(contactOptions || []);
      setInvestidorOptions(contactOptions || []);
    }
  }, [opened, contactOptions]);

  const handleEditPackage = (products: Product[]) => {
    setInitialPackage(products);
    setProductModalOpened(true);
  };

  const saveProducts = (products: Product[]) => {
    setOppForm(previous => ({ ...previous, products: [
      ...previous.products.filter(item => !initialPackage?.[0] || !belongsToPackage(item, initialPackage[0])),
      ...products,
    ] }));
  };

  const deletePackage = (product: Product) => {
    setOppForm(previous => ({ ...previous, products: previous.products.filter(item => !belongsToPackage(item, product)) }));
  };

  const handleClose = () => {
    setOppForm(initialOpportunityForm);
    setAutoImportFile(null);
    setImportErrors([]);
    setNotification(null);
    setModalMode('manual');
    setProductModalOpened(false);
    setAttachmentErrors([]);
    onClose();
  };

  const getOppAttachmentSource = (attachment: OpportunityFile): AttachmentFileSource | null => {
    const localFile = attachment.file instanceof File ? attachment.file : undefined;
    const filePath = typeof attachment.file === 'string' ? attachment.file : undefined;
    return attachmentSourceFromFields(attachment.file_name, filePath, localFile);
  };

  const downloadOppAttachment = async (attachment: OpportunityFile) => {
    const source = getOppAttachmentSource(attachment);
    if (!source) return;
    setDownloadingAttachmentId(attachment.id);
    try {
      await downloadAttachment(source, accessToken);
    } catch {
      setAttachmentErrors([t('common.attachments.downloadFailed')]);
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const isSavedClosed = !!editingOpportunity && (getCurrentStageForOpportunity(editingOpportunity).name || '').toLowerCase() === 'closed';
  const currentOpenStage = oppForm.stages.find((st) => st.finished_at == null);
  const isClosingInForm = (currentOpenStage?.name || '').toLowerCase() === 'closed';


  return (
    <>
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      title={<Group position="apart" spacing="sm" className="opportunity-header-content">
        <Text size="lg" weight={500}>{modalMode === 'manual' ? editingOpportunity ? t('opportunity.modal.edit') : t('opportunity.modal.add') : modalMode === 'automático' ? t('opportunity.modal.import') : t('opportunity.modal.attachmentsTitle')}</Text>
        <Group spacing="xs">
          <Button variant="outline" leftIcon={<IconFile size={16}/>} onClick={(event) => { event.currentTarget.blur(); setQuoteOpened(true); }}
          disabled={!oppForm.products.length || !oppForm.client_ids.length || !oppForm.name.trim()}>
          {t('opportunity.quote.button')}
        </Button>
          {editingOpportunity && <Button variant="light" onClick={() => {
            handleClose();
            navigate(buildPageUrl('/invoices', { opportunityId: editingOpportunity.id }));
          }}>{t('opportunity.modal.viewInvoices')}</Button>}
        </Group>
      </Group>}

      size="xl"
      classNames={{ content: 'opportunity-modal-content', body: 'opportunity-modal-body' }}
      styles={{ body: { padding: '0' }, title: { flex: 1, marginRight: 12 }, header: { flexShrink: 0 } }}
    >
      <Tabs className="opportunity-modal-tabs"
        value={modalMode}
        onTabChange={(value) => setModalMode(value as 'manual' | 'automático' | 'anexos')}
      >

        <Tabs.List>
          {/* {!editingOpportunity && <Tabs.Tab value="manual">Data</Tabs.Tab>} */}
          <Tabs.Tab value="manual">{t('opportunity.modal.data')}</Tabs.Tab>
          <Tabs.Tab value="anexos">{t('opportunity.modal.attachment')}</Tabs.Tab>
          {/*{!editingOpportunity && (*/}
          {/*  <Tabs.Tab value="automático">Automatic</Tabs.Tab>*/}
          {/*)}*/}
        </Tabs.List>

        {/* ─── MANUAL ───────────────────────── */}
        <Tabs.Panel value="manual" pt="xs">
          <div className="opportunity-form-scroll">
            <div style={{ padding: '1rem' }}>
              <div className="opportunity-details-grid">
              <Select
                className="opportunity-owner-field"
                required
                label={t('opportunity.modal.owner')}
                placeholder={t('opportunity.modal.selectOwner')}
                data={ownerOptions || []}
                value={oppForm.owner ?? null}
                onChange={(value) => setOppForm({ ...oppForm, owner: value })}
                clearable
                searchable
              />
              <Select
                className="opportunity-campaign-field"
                label={t('opportunity.modal.campaign')}
                placeholder={t('opportunity.modal.selectCampaign')}
                data={campaignOptions || []}
                value={oppForm.campaign_id}
                onChange={(value) => { setOppForm({ ...oppForm, campaign_id: value || '' }) }}
                clearable
                searchable
              />
                <DateInput
                  className="opportunity-date-field" label={t('opportunity.modal.startDate')}
                  placeholder={t('opportunity.modal.selectStartDate')}
                  value={calendarDate(oppForm.startDate)}
                  onChange={(date) => {
                    setOppForm({ ...oppForm, startDate: date ? date.toISOString() : '' });
                  }}
                  required
                />
                <TextInput
                  className="opportunity-name-field"
                  label={t('opportunity.modal.name')}
                  placeholder={t('opportunity.modal.namePlaceholder')}
                  value={oppForm.name}
                  onChange={(e) => setOppForm({ ...oppForm, name: e.target.value })}
                  required
                />
                {editingOpportunity ? (
                  <Select
                    className="opportunity-stage-field" label={t('opportunity.modal.stage')}
                    placeholder={t('opportunity.modal.selectStage')}
                    data={stageOptions}
                    value={oppForm.stages.filter(st => st.finished_at === null || st.finished_at === undefined)[0]?.name || ''}
                    disabled={isSavedClosed}
                    onChange={(value: string) => {
                      if (value) {
                        if (oppForm.stages.length) {
                          let old = oppForm.stages.filter(st => st.finished_at === null || st.finished_at === undefined)[0]
                          // old.name = value
                          setOppForm({ ...oppForm, stages: [{ ...old, name: value, finished_at: undefined }] });
                        } else {
                          setOppForm({
                            ...oppForm,
                            stages: [{
                              id: `s-${Date.now()}`,
                              opportunity_id: '',
                              name: value,
                              created_at: oppForm.startDate || new Date().toISOString().split('T')[0]
                            }]
                          });
                        }
                      }
                    }}
                    required
                  />
                ) : (
                  <TextInput className="opportunity-stage-field" label={t('opportunity.modal.stage')} value={getStageLabel('Value Proposition', t)} disabled />
                )}

                <MultiSelect
                  className="opportunity-clients-field"
                  label={t('opportunity.modal.clients')}
                  placeholder={isClosingInForm ? t('opportunity.modal.requiredToClose') : t('opportunity.modal.selectClients')}
                  data={clientOptions || []}
                  value={oppForm.client_ids}
                  onChange={(values) => {
                    setOppForm({
                      ...oppForm,
                      client_ids: values,
                    });
                    setDesisionMakerOptions(contactOptions || []);
                    setInfluencerOptions(contactOptions || []);
                    setInvestidorOptions(contactOptions || []);
                  }}
                  clearable
                  searchable
                  required={isClosingInForm}
                  error={
                    isClosingInForm && !oppForm.client_ids.length
                      ? t('opportunity.modal.clientRequiredError')
                      : undefined
                  }
                />


              <Group className="opportunity-contact-field" align="flex-end" noWrap spacing="xs">
                <Select
                  label={t('opportunity.modal.contact')}
                  placeholder={t('opportunity.modal.selectContact')}
                  data={contactOptions || []}
                  value={oppForm.contact_id || null}
                  onChange={(value) => setOppForm({ ...oppForm, contact_id: value || '' })}
                  required
                  searchable
                  clearable
                  style={{ flex: 1 }}
                />
                <ActionIcon
                  onClick={() => {
                    setNewContactForm({
                      first_name: '',
                      last_name: '',
                      email: '',
                      phone_number: '',
                      position: '',
                    });
                    setContactModalOpened(true);
                  }}
                  variant="light"
                  title={t('opportunity.modal.addContact')}
                  sx={{
                    backgroundColor: 'rgba(48,113,91,0.2)',
                    width: '36px',
                    height: '36px',
                  }}
                >
                  <IconPlus size={16} />
                </ActionIcon>
              </Group>

              <Select
                className="opportunity-decision-field" label={t('opportunity.modal.decisionMaker')}
                placeholder={t('opportunity.modal.selectDecisionMaker')}
                data={decisionMakerOptions || []}
                value={oppForm.decision_maker}
                onChange={(value) => { setOppForm({ ...oppForm, decision_maker: value || '' }) }}
                clearable
                searchable
              />
              <Select
                className="opportunity-influencer-field" label={t('opportunity.modal.influencer')}
                placeholder={t('opportunity.modal.selectInfluencer')}
                data={influencerOptions || []}
                value={oppForm.influencer}
                onChange={(value) => setOppForm({ ...oppForm, influencer: value || '' })}
                clearable
                searchable
              />
              <Select
                className="opportunity-investor-field" label={t('opportunity.modal.investor')}
                placeholder={t('opportunity.modal.selectInvestor')}
                data={investidorOptions || []}
                value={oppForm.investidor}
                onChange={(value) => setOppForm({ ...oppForm, investidor: value || '' })}
                clearable
                searchable
              />
              <TextInput
                className="opportunity-remito-field"
                label={t('opportunity.modal.remito')}
                placeholder={t('opportunity.modal.remito')}
                value={oppForm.remito}
                onChange={(e) => setOppForm({ ...oppForm, remito: e.target.value })}
              />

              </div>

              <Group spacing="xs" align="center" mb="xs">
                <Text weight={600} size="md">
                  {t('opportunity.modal.salesPackages')}
                </Text>
                <ActionIcon
                  aria-label={t('opportunity.modal.addSalesPackage')}
                  title={t('opportunity.modal.addSalesPackage')}
                  onClick={() => { setInitialPackage(null); setProductModalOpened(true) }}
                  variant="light"
                  sx={{
                    backgroundColor: 'rgba(48,113,91,0.2)',
                    width: '32px',
                    height: '32px',
                  }}
                >
                  <IconPlus size={16} />
                </ActionIcon>
              </Group>

              <Stack spacing="xs" mb="sm">
                <SalesPackageTable
                  products={oppForm.products}
                  onEdit={handleEditPackage}
                  onDelete={items => setOppForm(previous => ({ ...previous, products: previous.products.filter(product => !items.includes(product)) }))}
                  onSold={(items, sold) => setOppForm(previous => ({ ...previous, products: previous.products.map(product => items.includes(product) ? { ...product, sold } : product) }))}
                />
                <Checkbox
                  label={t('opportunity.modal.noSalesMade')}
                  checked={oppForm.noSalesMade}
                  onChange={(event) => {
                    setNoSalesMade(event.currentTarget.checked)
                    setOppForm({ ...oppForm, noSalesMade: event.currentTarget.checked })
                  }}
                  mb="md"
                />
              </Stack>
            </div>
          </div>
        </Tabs.Panel>

        {/* ─── AUTOMÁTICO ────────────────────── */}
        <Tabs.Panel value="automático" pt="xs">
          <div
            {...getRootProps()}
            style={{
              padding: '1rem',
              border: '1px dashed #ccc',
              textAlign: 'center',
              borderRadius: '8px',
            }}
          >
            <input {...getInputProps()} />
            {autoImportFile ? (
              <Group style={{ flexDirection: 'column' }} spacing="xs">
                <IconFileExcel size={50} color={theme.colors.green[6]} stroke={1.5} />
                <Text align="center" weight={700} size="lg">
                  {autoImportFile.name}
                </Text>
              </Group>
            ) : (
              <>
                {isDragActive ? (
                  <IconCloudUpload size={50} color={theme.colors.blue[6]} stroke={1.5} />
                ) : (
                  <IconCloudUpload size={50} stroke={1.5} />
                )}
                <Text align="center" weight={700} size="lg" mt="xl">
                  {t('opportunity.modal.dropExcel')}
                </Text>
                <Text align="center" size="sm" mt="xs" color="dimmed">
                  {t('opportunity.modal.excelHint')}
                </Text>
              </>
            )}
          </div>
          <div style={{ marginTop: '16px' }}>
            {importErrors.length > 0 && (
              <Group spacing="xs">
                {importErrors.map((error, index) => (
                  <Text key={index} color="red">
                    {error}
                  </Text>
                ))}
              </Group>
            )}
            {notification && (
              <Text color={notification.color} align="center">
                {notification.message}
              </Text>
            )}
          </div>
          <Group mt="md" position="apart" align="center">
            <Group
              spacing="xs"
              style={{ cursor: 'pointer' }}
              onClick={generateExcelTemplate}
            >
              <IconFileExcel size={24} stroke={1.5} color={theme.colors.green[6]} />
              <Text size="sm" weight={700} style={{ textDecoration: 'underline' }} color={theme.colors.green[6]}>
                {t('common.template')}
              </Text>
            </Group>
            <Button onClick={handleAutoImport} disabled={!autoImportFile}>
              {t('common.send')}
            </Button>
          </Group>
        </Tabs.Panel>

        {/* ─── ANEXOS ────────────────────────── */}
        <Tabs.Panel value="anexos" pt="xs">
          <div style={{ padding: '1rem' }}>
            <Stack spacing="sm">
              <div
                {...getRootPropsAnexos()}
                style={{
                  padding: '1rem',
                  border: '1px dashed #ccc',
                  textAlign: 'center',
                  borderRadius: '8px',
                  backgroundColor: isDragActiveAnexos
                    ? theme.colors.gray[0]
                    : undefined,
                  cursor: 'pointer',
                }}
              >
                <input {...getInputPropsAnexos()} multiple />
                {isDragActiveAnexos ? (
                  <IconFile size={50} color={theme.colors.red[8]} stroke={1.5} />
                ) : (
                  <IconFile size={50} color={theme.colors.red[6]} stroke={1.5} />
                )}
                {isDragActiveAnexos ? (
                  <Text align="center" weight={700} size="lg" mt="xl" color={theme.colors.red[8]}>
                    {t('opportunity.modal.dropPdfActive')}
                  </Text>
                ) : (
                  <Text align="center" weight={700} size="lg" mt="xl">
                    {t('opportunity.modal.dropPdf')}
                  </Text>
                )}

                <Text align="center" size="sm" mt="xs" color="dimmed">
                  {t('opportunity.modal.pdfHint')}
                </Text>
              </div>

              {attachmentErrors.length > 0 && (
                <Group spacing="xs">
                  {attachmentErrors.map((err, idx) => (
                    <Text key={idx} color="red" size="sm">
                      {err}
                    </Text>
                  ))}
                </Group>
              )}
              <Table highlightOnHover withBorder striped>
                <thead>
                  <tr>
                    <th>{t('opportunity.modal.fileName')}</th>
                    <th>{t('opportunity.modal.fileSize')}</th>
                    <th><DateColumnHeader label={t('opportunity.modal.uploadDate')} dateTime/></th>
                    <th>{t('tasks.form.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {oppForm?.files.map((att) => (
                    <tr key={att.id}>
                      <td>{att.file_name}</td>
                      <td>
                        {(att.size / (1024 * 1024)).toFixed(2)} MB
                      </td>
                      <td>{formatAppDateTime(att.created_at)}</td>
                      <td>
                        {getOppAttachmentSource(att) ? (
                          <AttachmentActionButtons
                            onPreview={() => {
                              const source = getOppAttachmentSource(att);
                              if (source) openPreview(source);
                            }}
                            onDownload={() => void downloadOppAttachment(att)}
                            onRemove={() => removeAttachment(att.id)}
                            downloadLoading={downloadingAttachmentId === att.id}
                            showRemove
                          />
                        ) : (
                          <AttachmentActionButtons
                            onPreview={() => {}}
                            onDownload={() => {}}
                            onRemove={() => removeAttachment(att.id)}
                            showPreview={false}
                            showDownload={false}
                            showRemove
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Stack>
          </div>
        </Tabs.Panel>
      </Tabs>
      <Group className="opportunity-modal-footer" position="right">

        {editingOpportunity && onDelete && (
          <Button
            color="red"
            onClick={onDelete} mr="sm"
            sx={{
              backgroundColor: 'rgba(163, 0, 0, 0.9)',
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(121, 0, 0, 0.9)' },
            }}>
            {t('common.delete')}
          </Button>
        )}
        <Button
          onClick={editingOpportunity ? () => onUpdate(oppForm, editingOpportunity.id) : saveOpportunity}
          disabled={validateFileds()}>{editingOpportunity ? t('common.update') : t('common.save')}
        </Button>
      </Group>
      <OpportunityQuoteModal opened={quoteOpened && opened} onClose={() => setQuoteOpened(false)} opportunity={oppForm}
        clientNames={oppForm.client_ids.map(id => clientOptions?.find(client => client.value === id)?.label || id)} clientOptions={clientOptions||[]} contactOptions={contactOptions||[]} />
      <OpportunityProductsModal
        opened={productModalOpened}
        onClose={() => setProductModalOpened(false)}
        onSave={saveProducts}
        productOptions={productOptions}
        products={products}
        initialProducts={initialPackage || undefined}
        existingProducts={oppForm.products}
        onDelete={deletePackage}
        opportunityClientOptions={oppForm.client_ids
          .map((id) => clientOptions?.find((opt) => opt.value === id))
          .filter((opt): opt is Options => !!opt)}
      />

      <Modal
        opened={contactModalOpened}
        onClose={() => setContactModalOpened(false)}
        title={t('opportunity.modal.newContact')}
        centered
        size="md"
      >
        <Stack spacing="sm">
          <TextInput
            label={t('opportunity.modal.name')}
            value={newContactForm.first_name}
            onChange={(e) => setNewContactForm({ ...newContactForm, first_name: e.target.value })}
            required
          />
          <TextInput
            label={t('opportunity.modal.surname')}
            value={newContactForm.last_name}
            onChange={(e) => setNewContactForm({ ...newContactForm, last_name: e.target.value })}
            required
          />
          <TextInput
            label={t('settings.email')}
            type="email"
            value={newContactForm.email}
            onChange={(e) => setNewContactForm({ ...newContactForm, email: e.target.value })}
            required
          />
          <TextInput
            label={t('opportunity.modal.phoneNumber')}
            value={newContactForm.phone_number}
            onChange={(e) => setNewContactForm({ ...newContactForm, phone_number: e.target.value })}
            required
          />
          <TextInput
            label={t('opportunity.modal.position')}
            value={newContactForm.position}
            onChange={(e) => setNewContactForm({ ...newContactForm, position: e.target.value })}
            required
          />
          <Group position="right" mt="sm">
            <Button variant="default" onClick={() => setContactModalOpened(false)}>{t('common.cancel')}</Button>
            <Button
              loading={contactSaving}
              disabled={
                !newContactForm.first_name ||
                !newContactForm.last_name ||
                !newContactForm.email ||
                !newContactForm.phone_number ||
                !newContactForm.position ||
                !onCreateContact
              }
              onClick={async () => {
                if (!onCreateContact) return;
                setContactSaving(true);
                try {
                  const created = await onCreateContact({
                    ...newContactForm,
                    client_ids: oppForm.client_ids.length ? oppForm.client_ids : undefined,
                  });
                  setOppForm((prev) => ({ ...prev, contact_id: created.value }));
                  setContactModalOpened(false);
                } catch (err) {
                  console.error('Failed to create contact:', err);
                } finally {
                  setContactSaving(false);
                }
              }}
            >
              {t('opportunity.modal.saveContact')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
    {previewModal}
    </>
  );
};
